import {
    getFirestore, collection, query, where, getDocs,
    doc, updateDoc, serverTimestamp, orderBy, limit, runTransaction, startAfter
} from "firebase/firestore";
import { app } from "../firebase-config.js";
import { InAppNotificationService } from "./in-app-notification.service.js";

const db = getFirestore(app);

export const AdminReportService = {
    /**
     * Fetch Unresolved Reports (Queue)
     * Ordered by oldest first usually, to deal with backlog.
     */
    /**
     * Fetch Unresolved Reports (Queue) with Pagination & Filtering
     * @param {Object} lastDoc - for pagination (startAfter)
     * @param {number} limitCount - default 20
     * @param {string} filterSource - 'all' | 'enom' | 'general'
     */
    fetchActiveReports: async (lastDoc = null, limitCount = 20, filterSource = 'all') => {
        try {
            // [CRITICAL FIX v0.809]
            // Legacy Data uses 'status': 'resolved'/'pending'
            // New Data uses 'isResolved': true/false
            // Querying by `isResolved == false` HIDES all Legacy data.
            // 
            // Strat: Fetch latest 100 items (regardless of status) and filter in memory.
            // This ensures we catch mixed schemas.

            let q = query(collection(db, "reports"),
                orderBy("createdAt", "desc"), // Newest first
                limit(100)
            );

            // [FIX v0.810] Infinite Scroll Fix
            // If lastDoc is provided, we MUST use startAfter
            if (lastDoc) {
                q = query(collection(db, "reports"),
                    orderBy("createdAt", "desc"),
                    startAfter(lastDoc),
                    limit(100)
                );
            }

            const snapshot = await getDocs(q);
            let rawData = snapshot.docs.map(doc => {
                const d = doc.data();
                return {
                    id: doc.id,
                    ...d,
                    // Normalize Status for internal logic
                    _isResolved: d.isResolved === true || d.status === 'resolved',
                    // Normalize Source
                    _source: (d.source || 'general').toLowerCase(),
                    _doc: doc
                };
            });

            // 1. Filter for "Active" (Unresolved)
            let activeReports = rawData.filter(r => !r._isResolved);

            // 2. Filter by Source Tab
            if (filterSource && filterSource !== 'all') {
                const target = filterSource.toLowerCase();
                activeReports = activeReports.filter(r => r._source === target);
            }

            // 3. Return correct cursor
            // We must return the LAST doc of the fetched batch to serve as the cursor for the next batch.
            // Even if we filtered out everything, we need the cursor of the DB scan to proceed.
            const nextCursor = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

            return {
                data: activeReports,
                lastDoc: nextCursor,
                hasMore: snapshot.docs.length === 100 // If we got full batch, there might be more
            };
        } catch (error) {
            console.error("AdminReportService.fetchActiveReports Error:", error);
            throw error;
        }
    },

    /**
     * Fetch Resolved History (Recent)
     */
    fetchResolvedHistory: async (limitCount = 30) => {
        try {
            const q = query(
                collection(db, "reports"),
                where("isResolved", "==", true),
                orderBy("resolvedAt", "desc"), // Restored
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("AdminReportService.fetchResolvedHistory Error:", error);
            throw error;
        }
    },

    /**
     * Execute Judgment (Ban, Suspend, or Dismiss)
     * Wraps User status update, Report resolution, and Notification in a transaction/flow.
     * @param {string} reportId 
     * @param {string} targetUserId 
     * @param {string} action - 'ban' | 'suspend' | 'dismiss'
     * @param {string} adminNote - Internal note
     */
    executeJudgement: async (reportId, targetUserId, action, adminNote = "") => {
        try {
            await runTransaction(db, async (transaction) => {
                const reportRef = doc(db, "reports", reportId);
                const userRef = doc(db, "users", targetUserId);

                // 1. Update User Status (if penalizing)
                if (action === 'ban') {
                    transaction.update(userRef, {
                        status: 'banned',
                        bannedAt: serverTimestamp(),
                        banReason: adminNote
                    });
                } else if (action === 'suspend') {
                    const releaseDate = new Date();
                    releaseDate.setDate(releaseDate.getDate() + 3); // 3 Day Suspension
                    transaction.update(userRef, {
                        status: 'suspended',
                        suspendedUntil: releaseDate,
                        suspendReason: adminNote
                    });
                }

                // 2. Resolve Report
                transaction.update(reportRef, {
                    isResolved: true,
                    resolution: action, // 'ban', 'suspend', 'dismiss'
                    adminNote: adminNote,
                    resolvedAt: serverTimestamp()
                });
            });

            // 3. Notify User (Non-blocking, after transaction)
            if (action !== 'dismiss') {
                const title = action === 'ban' ? '계정이 영구 정지되었습니다.' : '계정이 3일간 정지되었습니다.';
                const msg = `사유: ${adminNote}`;
                // We might not be able to notify if they are banned/suspended depending on logic, 
                // but we try to insert the notif so they see it if they can log in or appeal.
                // Or maybe email trigger handles this. For now, in-app notif.
                InAppNotificationService.createNotification(
                    targetUserId,
                    'system',
                    title,
                    msg,
                    null
                );
            }

            return true;
        } catch (error) {
            console.error("AdminReportService.executeJudgement Error:", error);
            throw error;
        }
    }
};
